import React, { useState, useEffect, useRef } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import { CheckCircle, Loader2 } from 'lucide-react';
import { authAPI } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import AuthLayout from '../components/AuthLayout';
import AuthField from '../components/AuthField';
import FormError from '../components/FormError';

const ResetPassword = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { logout } = useAuth();
  const token = searchParams.get('token');
  const [formData, setFormData] = useState({ newPassword: '', confirmPassword: '' });
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');
  const [fieldErrors, setFieldErrors] = useState({});
  const [linkState, setLinkState] = useState(token ? 'checking' : 'missing');
  const [attempt, setAttempt] = useState(0);
  const titleRef = useRef(null);

  useEffect(() => {
    let cancelled = false;
    if (!token) { setLinkState('missing'); return undefined; }
    setLinkState('checking');
    authAPI.validateResetToken(token).then(() => {
      if (!cancelled) setLinkState('ready');
    }).catch((err) => {
      if (cancelled) return;
      setLinkState(err.response?.data?.code === 'RESET_LINK_EXPIRED' ? 'expired'
        : err.response?.data?.code === 'RESET_LINK_INVALID' ? 'invalid' : 'unavailable');
    });
    return () => { cancelled = true; };
  }, [token, attempt]);

  useEffect(() => {
    if (linkState !== 'checking' || success) titleRef.current?.focus();
  }, [linkState, success]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (loading || linkState !== 'ready') return;
    setError('');
    const errors = {};
    if (formData.newPassword.length < 6) errors.newPassword = 'Use at least 6 characters.';
    if (!formData.confirmPassword) errors.confirmPassword = 'Confirm your new password.';
    else if (formData.newPassword !== formData.confirmPassword) errors.confirmPassword = 'Passwords do not match.';
    setFieldErrors(errors);
    if (Object.keys(errors).length) {
      document.getElementById(`resetpassword-${Object.keys(errors)[0]}`)?.focus(); return;
    }
    setLoading(true);
    try {
      await authAPI.resetPassword(token, formData.newPassword);
      setSuccess(true);
      setFormData({ newPassword: '', confirmPassword: '' });
      navigate('/reset-password', { replace: true });
    } catch (err) {
      const code = err.response?.data?.code;
      if (code === 'RESET_LINK_EXPIRED' || code === 'RESET_LINK_INVALID') {
        setLinkState(code === 'RESET_LINK_EXPIRED' ? 'expired' : 'invalid');
      } else {
        setError(!err.response ? 'Cannot reach the server. Check your connection and try again.'
          : err.response.status >= 500 ? 'Password reset is temporarily unavailable. Please try again shortly.'
            : err.response?.data?.error || 'Could not update your password. Please try again.');
      }
    } finally { setLoading(false); }
  };

  const primaryLink = 'flex w-full items-center justify-center rounded-lg bg-primary-600 px-4 py-3 text-sm font-semibold text-white hover:bg-primary-700';
  if (success) return (
    <AuthLayout title="Your password is updated" titleRef={titleRef} description="You’re ready to sign in with your new password.">
      <CheckCircle aria-hidden="true" className="mb-5 h-10 w-10 text-whatsapp-600" />
      <Link to="/login" onClick={logout} className={primaryLink}>Sign in with new password</Link>
    </AuthLayout>
  );

  if (linkState !== 'ready') {
    const titles = { checking: 'Checking your reset link', missing: 'You’ll need a reset link', expired: 'This reset link has expired', invalid: 'This reset link isn’t valid', unavailable: 'We couldn’t check your link' };
    const descriptions = { checking: 'This should only take a moment.', missing: 'Open the link in your LeadMarka reset email, or request a new one below.', expired: 'Reset links expire after 1 hour. Request a new one to choose your password.', invalid: 'The link may be incomplete or damaged. Open the full link from your email, or request a new one.', unavailable: 'Check your connection and try again. You don’t need to request another email yet.' };
    return (
      <AuthLayout title={titles[linkState]} description={descriptions[linkState]} titleRef={titleRef}>
        {linkState === 'checking' ? <p role="status" className="flex items-center gap-2 text-sm text-gray-600"><Loader2 aria-hidden="true" className="h-5 w-5 animate-spin" />Checking link...</p>
          : linkState === 'unavailable' ? <button type="button" onClick={() => setAttempt(attempt + 1)} className={primaryLink}>Try again</button>
            : <Link to="/forgot-password" className={primaryLink}>Request a new reset link</Link>}
        <Link to="/login" className="mt-4 flex items-center justify-center text-sm font-medium text-primary-600 hover:text-primary-700">Back to sign in</Link>
      </AuthLayout>
    );
  }

  const updateField = (name, value) => {
    setFormData((previous) => ({ ...previous, [name]: value }));
    setFieldErrors((previous) => ({ ...previous, [name]: '', ...(name === 'newPassword' ? { confirmPassword: '' } : {}) }));
  };
  return (
    <AuthLayout title="Choose a new password" titleRef={titleRef} description="Use a password you don’t use for other accounts.">
      <form onSubmit={handleSubmit} noValidate className="space-y-5" aria-busy={loading}>
        <FormError message={error} />
        <AuthField id="resetpassword-newPassword" label="New password" autoComplete="new-password" type={showPassword ? 'text' : 'password'}
          value={formData.newPassword} onChange={(e) => updateField('newPassword', e.target.value)} required minLength={6} disabled={loading}
          error={fieldErrors.newPassword} hint="Use at least 6 characters. A longer password is more secure."
          showPassword={showPassword} onTogglePassword={() => setShowPassword(!showPassword)} />
        <AuthField id="resetpassword-confirmPassword" label="Confirm new password" autoComplete="new-password" type={showPassword ? 'text' : 'password'}
          value={formData.confirmPassword} onChange={(e) => updateField('confirmPassword', e.target.value)} required disabled={loading} error={fieldErrors.confirmPassword} />
        <button type="submit" disabled={loading} className={`${primaryLink} gap-2 disabled:cursor-not-allowed disabled:opacity-60`}>
          {loading && <Loader2 aria-hidden="true" className="h-4 w-4 animate-spin" />}{loading ? 'Saving password...' : 'Save new password'}
        </button>
      </form>
      <p role="status" className="sr-only">{loading ? 'Saving your new password.' : ''}</p>
      <Link to="/forgot-password" className="mt-4 flex items-center justify-center text-sm font-medium text-primary-600 hover:text-primary-700">Need a new reset link?</Link>
    </AuthLayout>
  );
};

export default ResetPassword;
